import { useEffect } from 'react';
import { throttleToAnimationFrame, isPlatformNodeJS } from '@utils/common';
import { makeSelectors, combineTokens, makeLengthToken } from '@utils/css';

/**
 * Exports a collection of commonly used units, which should be favored over
 * using regular units (e.g. ${units.px(10)} instead of 10px).
 *
 * The reason for favoring these units is two-fold:
 *
 * 1) keeping units as CSS Variables allow hot-swapping values per-brand
 *    (e.g. one brand may need components rendered 10% larger which can easily
 *          be achieved by units.override() and setting the "px" to be
 *          slightly larger.)
 *
 * 2) As rem is still generally favored over px in order to respect user zoom
 *    etc. then unit.px() can be configured to use rem under the hood while
 *    developers can implement styling using the familiar px unit.
 */
const baseUnits = combineTokens("--unit", {
  px: ["1px", makeLengthToken],
  vh: ["1vh", makeLengthToken],
  vw: ["1vw", makeLengthToken],
  /**
   * resolves to the currently visible viewport height. In contrast to the
   * regular vh() unit, this unit takes the height of virtual keyboards etc.
   * into account.
   */
  vvh: ["1vh", makeLengthToken]
});
const units = Object.assign(baseUnits, {
  font: {
    px: makeLengthToken("--unit--font-px", baseUnits.px(1))
  }
});
const virtualKeyboardOpened = makeSelectors("virtual-keyboard-visible");

/**
 * applies a --vh: [px] unit, which is automatically calculated based on the
 * window.innerHeight prop.
 *
 * we do this, because most mobile devices implement vh unit based on the false
 * assumption that their own UI shouldn't be included. the result is often that
 * layers which we expect to fit within the visible viewport slightly exceeds
 * it causing annoyance for the user.
 */
function useVhUnit() {
  useEffect(() => {
    const supportsDVH = CSS.supports("height: 1dvh");
    if (supportsDVH) {
      return;
    }
    const updateVhUnit = () => {
      document.body.style.setProperty(units.vh.propName, units.px(window.innerHeight / 100));
      document.body.style.setProperty(units.vvh.propName, units.px((window.visualViewport?.height ?? window.innerHeight) / 100));
      if (window.visualViewport) {
        document.body.classList.toggle(virtualKeyboardOpened.className, window.visualViewport.height < window.innerHeight);
      }
    };
    updateVhUnit();
    const onResize = throttleToAnimationFrame(updateVhUnit);
    window.visualViewport?.addEventListener("rezize", onResize);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.visualViewport?.addEventListener("rezize", onResize);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
}
const cachedElements = new WeakMap();

/**
 * simply utility that allows extracting a JavaScript pixel number from a given
 * css token unit value
 *
 * @example getUnitValue(units.px(100)) -> 100
 */
function getUnitValue(rawUnit, root = document.body, strategy) {
  if (isPlatformNodeJS()) {
    return 0;
  }
  const resolvedRoot = "current" in root ? root.current ?? document.body : root;
  let cachedElement = cachedElements.get(resolvedRoot);
  if (!cachedElement) {
    cachedElement = document.createElement("div");
    cachedElement.style.cssText = `
      position: absolute;
      top: auto;
      right: -10000px;
      opacity: 0;
    `;
    resolvedRoot.appendChild(cachedElement);
    cachedElements.set(resolvedRoot, cachedElement);
  }
  cachedElement.style.width = rawUnit;
  const computedValue = strategy === "clientWidth" ? cachedElement.clientWidth : cachedElement.getBoundingClientRect().width;
  return computedValue;
}

export { getUnitValue, units, useVhUnit, virtualKeyboardOpened };
import "./units.linaria.module.css";
//# sourceMappingURL=units.js.map
